/**
 * Per-requirement / per-field audit of which Populate source tiers can
 * fill each authoredSchema key. Pinned by unit tests so registry drift
 * is caught at CI time.
 */
import {
  allChecklistItemsWithSchema,
  getFieldAlignment,
} from './requirementFieldAlignment';
import {
  allowedPopulateTiers,
  blockedPopulateTiers,
  type FieldPopulateSourceAudit,
  type PopulateSourceTier,
} from './populateSourcePolicy';

export type { FieldPopulateSourceAudit, PopulateSourceTier };

/** Build the full audit matrix for every checklist item with authoredSchema. */
export function buildPopulateSourceAudit(): FieldPopulateSourceAudit[] {
  const rows: FieldPopulateSourceAudit[] = [];
  for (const item of allChecklistItemsWithSchema()) {
    const structured = (item.authoredSchema ?? []).filter(f => f.key !== 'notes');
    for (const field of structured) {
      const alignment = getFieldAlignment(item.id, field.key);
      rows.push({
        requirementId: item.id,
        schemaKey: field.key,
        alignmentKind: alignment?.kind ?? 'unregistered',
        allowedTiers: allowedPopulateTiers(item.id, field.key),
        blockedTiers: blockedPopulateTiers(item.id, field.key),
      });
    }
  }
  return rows;
}

/** Lookup a single audit row. */
export function getFieldPopulateAudit(
  requirementId: string,
  schemaKey: string,
): FieldPopulateSourceAudit | undefined {
  return buildPopulateSourceAudit().find(
    r => r.requirementId === requirementId && r.schemaKey === schemaKey,
  );
}

/** Summarise consulted vs contributed channels for the detail panel. */
export interface PopulateSourceSummary {
  consulted: Partial<Record<PopulateSourceTier, number>>;
  /** Tiers that could contribute at least one field for this requirement. */
  canContribute: PopulateSourceTier[];
}

export function summarizePopulateSourcesForRequirement(
  requirementId: string,
  lineageCounts?: {
    damSources: number;
    providerSources: number;
    docSources: number;
    siteResearchSources: number;
    priorReportSources: number;
  },
): PopulateSourceSummary {
  const audit = buildPopulateSourceAudit().filter(r => r.requirementId === requirementId);
  const canContributeSet = new Set<PopulateSourceTier>();
  for (const row of audit) {
    for (const tier of row.allowedTiers) {
      if (tier !== 'user') canContributeSet.add(tier);
    }
  }
  const consulted: Partial<Record<PopulateSourceTier, number>> = {};
  if (lineageCounts) {
    if (lineageCounts.damSources > 0) consulted.dam = lineageCounts.damSources;
    if (lineageCounts.providerSources > 0) consulted.sed = lineageCounts.providerSources;
    if (lineageCounts.docSources > 0) consulted.doc = lineageCounts.docSources;
    if (lineageCounts.siteResearchSources > 0) consulted.research = lineageCounts.siteResearchSources;
  }
  // CAR is always "consulted" when the requirement has transform or car-eligible fields.
  if (canContributeSet.has('car')) consulted.car = 1;
  return {
    consulted,
    canContribute: Array.from(canContributeSet),
  };
}
